import {
    Box,
    Button,
    IconButton,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { GripVertical, HammerIcon, RotateCcw, X } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { DraggableSyntheticListeners } from '@dnd-kit/core';
import {
    getDefaultMarginName,
    getStructureColor,
    StructureOption,
} from './marginToolbarUtils';
import { validateStructureName } from '~/utils/validation';
import {
    STRUCTURE_NAME_MAX_LENGTH,
    generateUniqueStructureNameFromList,
} from '~/domain/services/dicomProcessor';

export type MarginMode = 'symmetric' | 'asymmetric';
export type MarginDirection = 'inner' | 'outer';

// Helpers

export const menuProps = {
    disablePortal: false,
    disableScrollLock: true,
    onClick: (e: React.MouseEvent) => e.stopPropagation(),
    onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
    slotProps: { paper: { sx: { maxHeight: 200, zIndex: 1500 } } },
};

export const Label = ({ children }: { children: React.ReactNode }) => (
    <Typography variant="body2" color="text.secondary" fontSize={12} mb={0.5}>
        {children}
    </Typography>
);

export const ColorDot = ({ color }: { color: string }) => (
    <Box
        sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: color,
            flexShrink: 0,
        }}
    />
);

interface LabeledSelectProps {
    label: string;
    value: number;
    onChange: (roiNumber: number) => void;
    options: { roiNumber: number; label: string; color: string }[];
    colorDot: string;
}

export function LabeledSelect({
    label,
    value,
    onChange,
    options,
    colorDot,
}: LabeledSelectProps) {
    const selectedLabel =
        options.find((o) => o.roiNumber === value)?.label ?? '';

    return (
        <Box>
            <Label>{label}</Label>
            <Select
                value={value.toString()}
                onChange={(e: SelectChangeEvent) =>
                    onChange(Number(e.target.value))
                }
                size="small"
                fullWidth
                sx={{ fontSize: 12 }}
                renderValue={() => (
                    <Stack direction="row" alignItems="center" gap={0.75}>
                        <ColorDot color={colorDot} />
                        <Typography fontSize={12} noWrap>
                            {selectedLabel}
                        </Typography>
                    </Stack>
                )}
                MenuProps={menuProps}
            >
                {options.map((o) => (
                    <MenuItem
                        key={o.roiNumber}
                        value={o.roiNumber.toString()}
                        sx={{ fontSize: 12 }}
                    >
                        <Stack direction="row" alignItems="center" gap={0.75}>
                            <ColorDot color={o.color} />
                            {o.label}
                        </Stack>
                    </MenuItem>
                ))}
            </Select>
        </Box>
    );
}

interface ActionButtonsProps {
    onApply: () => void;
    isApplyDisabled?: boolean;
}

const actionButtonSx = { textTransform: 'none', py: 0.75 };

export function ActionButtons({
    onApply,
    isApplyDisabled,
}: ActionButtonsProps) {
    return (
        <Button
            onClick={onApply}
            variant="contained"
            fullWidth
            size="small"
            disabled={isApplyDisabled}
            startIcon={<HammerIcon size={14} />}
            sx={actionButtonSx}
        >
            Apply
        </Button>
    );
}

// Header Component
interface HeaderProps {
    dragListeners: DraggableSyntheticListeners;
    onClose: () => void;
    isPreviewing: boolean;
    closeTitle: string;
}

export function Header({
    dragListeners,
    onClose,
    isPreviewing,
    closeTitle,
}: HeaderProps) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pb: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                gap={0.5}
                {...dragListeners}
                sx={{
                    cursor: 'grab',
                    '&:active': { cursor: 'grabbing' },
                    userSelect: 'none',
                    touchAction: 'none',
                    'aria-label': 'Drag to move margin tool',
                }}
            >
                <GripVertical size={12} style={{ opacity: 0.6 }} />
                <Typography fontSize={15} fontWeight={500}>
                    Margin
                </Typography>
            </Stack>
            <IconButton
                size="small"
                onClick={onClose}
                disabled={isPreviewing}
                sx={{ p: 0.25 }}
                title={closeTitle}
                aria-label={closeTitle}
            >
                <X size={18} />
            </IconButton>
        </Box>
    );
}

// OutputStructureSelect Component
interface OutputStructureSelectProps {
    label?: string;
    outputRoiNumber: number | null;
    setOutputRoiNumber: (v: number | null) => void;
    outputColor?: string;
    inputColor: string;
    inputRoiNumber: number;
    availableStructures: StructureOption[];
    customRoiName: string;
    setCustomRoiName: (v: string) => void;
    customNameError?: string | null;
    /** Placeholder name for the new-structure text field.
     *  When omitted, defaults to the input name with the margin suffix (_mrg),
     *  made unique against existing structure names. */
    defaultName?: string;
}

/** MUI Select sentinel value for "New Structure" */
const NEW_STRUCTURE_VALUE = 'new';

export function OutputStructureSelect({
    label = 'Output Structure',
    outputRoiNumber,
    setOutputRoiNumber,
    outputColor,
    inputColor,
    inputRoiNumber,
    availableStructures,
    customRoiName,
    setCustomRoiName,
    customNameError,
    defaultName,
}: OutputStructureSelectProps) {
    const inputStructure = availableStructures.find(
        (s) => s.roiNumber === inputRoiNumber
    );

    // Approved structures (ROI- or structure-set-level) cannot receive
    // operation results, so exclude them from the result-target list.
    const otherStructures = useMemo(
        () =>
            availableStructures.filter(
                (s) => s.roiNumber !== inputRoiNumber && !s.isApproved
            ),
        [availableStructures, inputRoiNumber]
    );

    // Keep outputRoiNumber matching a rendered MenuItem: drop it back to
    // null when the target is missing or has become approved.
    useEffect(() => {
        if (outputRoiNumber === null) {
            return;
        }
        const target = availableStructures.find(
            (s) => s.roiNumber === outputRoiNumber
        );
        if (!target || target.isApproved) {
            setOutputRoiNumber(null);
        }
    }, [outputRoiNumber, availableStructures, setOutputRoiNumber]);

    const existingNames = useMemo(
        () => availableStructures.map((s) => s.name),
        [availableStructures]
    );

    const computedDefaultName = useMemo(
        () =>
            defaultName === undefined
                ? generateUniqueStructureNameFromList(
                      existingNames,
                      getDefaultMarginName(inputStructure?.name ?? '')
                  )
                : defaultName,
        [defaultName, existingNames, inputStructure]
    );

    const selectValue =
        outputRoiNumber === null
            ? NEW_STRUCTURE_VALUE
            : outputRoiNumber.toString();

    const handleChange = (e: SelectChangeEvent) => {
        const val = e.target.value;
        setOutputRoiNumber(val === NEW_STRUCTURE_VALUE ? null : Number(val));
    };

    const displayName =
        outputRoiNumber === null
            ? 'New Structure'
            : (availableStructures.find((s) => s.roiNumber === outputRoiNumber)
                  ?.name ?? '');

    return (
        <Box>
            <Label>{label}</Label>
            <Select
                value={selectValue}
                onChange={handleChange}
                size="small"
                fullWidth
                sx={{ fontSize: 12 }}
                renderValue={() => (
                    <Stack direction="row" alignItems="center" gap={0.75}>
                        {outputColor && <ColorDot color={outputColor} />}
                        <Typography fontSize={12} noWrap>
                            {displayName}
                        </Typography>
                    </Stack>
                )}
                MenuProps={menuProps}
            >
                <MenuItem value={NEW_STRUCTURE_VALUE} sx={{ fontSize: 12 }}>
                    New Structure
                </MenuItem>
                {inputStructure && !inputStructure.isApproved && (
                    <MenuItem
                        value={inputRoiNumber.toString()}
                        sx={{ fontSize: 12 }}
                    >
                        <Stack direction="row" alignItems="center" gap={0.75}>
                            <ColorDot color={inputColor} />
                            {inputStructure.name} (Current)
                        </Stack>
                    </MenuItem>
                )}
                {otherStructures.map((s) => (
                    <MenuItem
                        key={s.roiNumber}
                        value={s.roiNumber.toString()}
                        sx={{ fontSize: 12 }}
                    >
                        <Stack direction="row" alignItems="center" gap={0.75}>
                            <ColorDot color={getStructureColor(s.color)} />
                            {s.name}
                        </Stack>
                    </MenuItem>
                ))}
            </Select>
            {outputRoiNumber === null && (
                <TextField
                    value={customRoiName}
                    label="Name for new structure"
                    onChange={(e) => setCustomRoiName(e.target.value)}
                    placeholder={computedDefaultName}
                    size="small"
                    fullWidth
                    sx={{ mt: 1.5 }}
                    inputProps={{
                        maxLength: STRUCTURE_NAME_MAX_LENGTH,
                        style: { fontSize: 12 },
                    }}
                    InputLabelProps={{ shrink: true }}
                    error={!!customNameError}
                    helperText={customNameError}
                />
            )}
        </Box>
    );
}

/**
 * Validate a custom ROI name against existing structure names.
 * Returns an error string or null if valid.
 * Empty names are allowed (the default name will be used).
 */
export function getCustomNameError(
    customRoiName: string,
    existingNames: string[]
): string | null {
    const trimmed = customRoiName.trim();
    if (trimmed === '') {
        return null; // empty means use default
    }
    const [valid, error] = validateStructureName(trimmed, existingNames);
    return valid ? null : error;
}

// MarginOptions Component
interface MarginOptionsProps {
    marginType: MarginDirection;
    setMarginType: (d: MarginDirection) => void;
    currentMode: MarginMode;
    setCurrentMode: (m: MarginMode) => void;
    onReset: () => void;
}

const toggleButtonSx = {
    '& .MuiToggleButton-root': {
        py: 0.5,
        textTransform: 'none',
        fontSize: 12,
    },
};

export function MarginOptions({
    marginType,
    setMarginType,
    currentMode,
    setCurrentMode,
    onReset,
}: MarginOptionsProps) {
    return (
        <Stack spacing={1}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
                <Typography fontSize={12} fontWeight={500} color="primary.main">
                    Margin Type
                </Typography>
                {currentMode === 'asymmetric' && (
                    <Button
                        onClick={onReset}
                        size="small"
                        variant="text"
                        startIcon={<RotateCcw size={12} />}
                        sx={{
                            textTransform: 'none',
                            fontSize: 11,
                            py: 0,
                            minWidth: 'auto',
                        }}
                    >
                        Reset
                    </Button>
                )}
            </Stack>
            <Stack direction="row" spacing={1}>
                <ToggleButtonGroup
                    value={marginType}
                    exclusive
                    onChange={(_, v) => v && setMarginType(v)}
                    size="small"
                    sx={toggleButtonSx}
                >
                    <ToggleButton value="outer">Expand</ToggleButton>
                    <ToggleButton value="inner">Contract</ToggleButton>
                </ToggleButtonGroup>
                <ToggleButtonGroup
                    value={currentMode}
                    exclusive
                    onChange={(_, v) => v && setCurrentMode(v)}
                    size="small"
                    sx={toggleButtonSx}
                >
                    <ToggleButton value="symmetric">Uniform</ToggleButton>
                    <ToggleButton value="asymmetric">Variable</ToggleButton>
                </ToggleButtonGroup>
            </Stack>
        </Stack>
    );
}
